/**
 * useCustomerDisplay Hook
 * Müşteri ekranı (ikinci ekran) BroadcastChannel entegrasyonu.
 * - BroadcastChannel oluşturur ve sepet değişikliklerini ikinci ekrana gönderir
 * - Standalone (paket/restoran DEĞİL) modda müşteri ekranı penceresini açar/kapatır
 *   (Electron: ikinci monitör; Chrome: window.open popup)
 * - Satış modu işaretlemesi (RestaurantScreen ile paylaşım için)
 *
 * SAHIPLENME KURALI:
 *   - Paket sipariş modunda müşteri ekranını PackageOrderScreen yönetir.
 *   - Restoran modunda müşteri ekranını RestaurantScreen yönetir (burası yalnızca
 *     BroadcastChannel ile sepet değişikliklerini bildirir; pencereyi AÇMAZ).
 *   - Standalone hızlı satışta yalnızca burası müşteri ekranını açar/kapatır.
 */

import { useEffect, useRef, useCallback } from 'react';
import ownAuthSession from '../../../../services/auth/ownAuthSession';
import { getCustomerDisplaySettings } from '../../../../services/salesService';
import type { CartItem } from '../types/product.types';

export interface UseCustomerDisplayParams {
  /** Aktif ekran ID (currentScreen?.id) */
  currentScreenId: string | undefined;
  /** Şirket ID */
  companyId: string | null;
  /** Paket sipariş modu mu? */
  isPackageMode: boolean;
  /** Restoran modu mu? */
  isRestaurantMode: boolean;
  /** Mevcut sepet */
  cart: CartItem[];
  /** Sepet toplamını hesaplayan fonksiyon */
  calculateTotalHook: () => number;
}

export interface UseCustomerDisplayReturn {
  customerDisplayChannelRef: React.MutableRefObject<BroadcastChannel | null>;
  notifyCustomerDisplaySaleComplete: () => void;
}

/**
 * Müşteri ekranı (ikinci ekran) entegrasyon hook'u
 */
export const useCustomerDisplay = ({
  currentScreenId,
  companyId,
  isPackageMode,
  isRestaurantMode,
  cart,
  calculateTotalHook,
}: UseCustomerDisplayParams): UseCustomerDisplayReturn => {
  const customerDisplayChannelRef = useRef<BroadcastChannel | null>(null);

  // BroadcastChannel oluştur ve sepet değişikliklerini gönder
  useEffect(() => {
    const screenIdNum = currentScreenId ? parseInt(currentScreenId) : null;
    if (!screenIdNum) return;

    // ✅ Paket sipariş modunda müşteri ekranını PackageOrderScreen yönetir;
    // QuickSaleScreen modaldan sepet/satış mesajları gönderilmemelidir.
    if (isPackageMode) {
      console.log('📡 [CustomerDisplay] Paket modu aktif — BroadcastChannel oluşturulmadı (müşteri ekranını paket sayfası yönetir)');
      return;
    }

    // BroadcastChannel oluştur
    const channelName = `customer-display-${screenIdNum}`;
    customerDisplayChannelRef.current = new BroadcastChannel(channelName);
    console.log('📡 [CustomerDisplay] BroadcastChannel oluşturuldu:', channelName);

    return () => {
      // Cleanup: BroadcastChannel kapat
      customerDisplayChannelRef.current?.close();
      customerDisplayChannelRef.current = null;
    };
  }, [currentScreenId, isPackageMode]);

  // ✅ Normal hızlı satış modunda müşteri ekranını kapat (restoran modu ve paket modu DEĞİLSE)
  useEffect(() => {
    if (isRestaurantMode || isPackageMode) {
      return;
    }

    return () => {
      // @ts-ignore
      const electronAPI = window.electronAPI;
      if (electronAPI?.customerDisplay) {
        electronAPI.customerDisplay.close();
        console.log('📺 [QuickSaleScreen] Electron müşteri ekranı kapatma isteği gönderildi');
      } else {
        // @ts-ignore
        const customerWindow = window.__customerDisplayWindow as Window | null;
        if (customerWindow && !customerWindow.closed) {
          console.log('📺 [QuickSaleScreen] Normal mod - Müşteri ekranı kapatılıyor...');
          try {
            customerWindow.close();
          } catch (err) {
            console.warn('📺 [QuickSaleScreen] Müşteri ekranı kapatılamadı:', err);
          }
          // @ts-ignore
          window.__customerDisplayWindow = null;
        }
      }
    };
  }, [isRestaurantMode, isPackageMode]);

  // ✅ Müşteri ekranını otomatik aç (isEnabled ise)
  // Electron: İkinci monitörde fullscreen BrowserWindow
  // Chrome: window.open ile popup
  //
  // 📺 SAHIBLENME KURALI:
  //   - Paket sipariş modunda müşteri ekranını PackageOrderScreen yönetir.
  //   - Restoran modunda müşteri ekranını RestaurantScreen yönetir
  //     (QuickSaleScreen sadece BroadcastChannel ile sepet değişikliklerini
  //     bildirmeye devam eder; pencereyi AÇMAZ).
  //   - Standalone (paket/restoran modu DEĞİL) hızlı satışta yalnızca burası
  //     müşteri ekranını açar/kapatır.
  // Bu kural sayesinde RestaurantScreen → QuickSaleScreen geçişinde tek
  // pencere kalır; aksi halde Electron mode'da Chrome popup'ın üstüne
  // ikinci bir Electron BrowserWindow açılıyordu.
  useEffect(() => {
    if (isPackageMode || isRestaurantMode) return;
    const screenIdNum = currentScreenId ? parseInt(currentScreenId) : null;
    if (!screenIdNum || !companyId) return;

    // @ts-ignore
    const electronAPI = window.electronAPI;
    const isElectron = !!electronAPI?.customerDisplay;

    // ✅ GLOBAL: Pencere zaten açıksa tekrar açma
    if (!isElectron) {
      // @ts-ignore
      const existingWindow = window.__customerDisplayWindow as Window | null;
      if (existingWindow && !existingWindow.closed) {
        console.log('📺 [CustomerDisplay] Pencere zaten açık (global), atlanıyor...');
        return;
      }
    }

    let isMounted = true;

    const checkAndOpenCustomerDisplay = async () => {
      if (!isMounted) return;

      try {
        console.log('📺 [CustomerDisplay] Ayarlar kontrol ediliyor...');

        let isEnabled = false;
        try {
          const response = await getCustomerDisplaySettings(screenIdNum);
          if (!isMounted) return;
          isEnabled = response.Success && response.Data?.IsEnabled === true;
        } catch (apiErr) {
          console.warn('📺 [CustomerDisplay] API hatası (devam ediliyor):', apiErr);
          return;
        }

        if (!isEnabled) {
          console.log('📺 [CustomerDisplay] Müşteri ekranı pasif veya ayarlar bulunamadı');
          return;
        }

        if (!isMounted) return;

        localStorage.setItem('__customerDisplayCompanyId', companyId);

        // Auth token'ı cache'le (yeni pencerede kendi oturum katmanı henüz hazır olmayabilir)
        try {
          const idToken = await ownAuthSession.getAccessToken();
          if (idToken) {
            localStorage.setItem('__customerDisplayAuthToken', idToken);
            console.log('📺 [CustomerDisplay] Auth token cache\'lendi');
          }
        } catch (tokenErr) {
          console.warn('📺 [CustomerDisplay] Auth token cache\'lenemedi:', tokenErr);
        }

        const displayUrl = `/#/customer-display/${screenIdNum}`;

        // ========== ELECTRON: İkinci monitörde fullscreen ==========
        if (isElectron) {
          console.log('📺 [CustomerDisplay] Electron algılandı, ikinci monitörde açılıyor...');

          try {
            const statusRes = await electronAPI.customerDisplay.isOpen();
            if (statusRes?.isOpen) {
              console.log('📺 [CustomerDisplay] Electron penceresi zaten açık, atlanıyor...');
              return;
            }
          } catch (e) { /* devam et */ }

          if (!isMounted) return;

          const baseUrl = window.location.origin || 'http://localhost:3003';
          const fullUrl = `${baseUrl}${displayUrl}`;

          try {
            const result = await electronAPI.customerDisplay.open(fullUrl);
            if (result?.success) {
              console.log('✅ [CustomerDisplay] Electron ikinci monitörde müşteri ekranı açıldı');
            } else {
              console.warn('⚠️ [CustomerDisplay] Electron müşteri ekranı açılamadı');
            }
          } catch (err) {
            console.error('❌ [CustomerDisplay] Electron müşteri ekranı hatası:', err);
          }
          return;
        }

        // ========== CHROME: window.open ile popup ==========
        console.log('📺 [CustomerDisplay] Chrome modu, popup açılıyor...');

        let targetLeft = window.screen.width;
        let targetTop = 0;
        let targetWidth = window.screen.width;
        let targetHeight = window.screen.height;

        try {
          if ('getScreenDetails' in window) {
            console.log('📺 [CustomerDisplay] Screen API destekleniyor, ekranlar alınıyor...');
            // @ts-ignore
            const screenDetails = await (window as any).getScreenDetails();
            if (!isMounted) return;

            const screens = screenDetails?.screens;
            if (screens && screens.length > 0) {
              console.log('📺 [CustomerDisplay] Bulunan ekranlar:', screens.length);

              const secondaryScreen = screens.find((s: any) => !s.isPrimary);
              if (secondaryScreen) {
                targetLeft = secondaryScreen.left || targetLeft;
                targetTop = secondaryScreen.top || 0;
                targetWidth = secondaryScreen.width || targetWidth;
                targetHeight = secondaryScreen.height || targetHeight;
                console.log('📺 [CustomerDisplay] ✅ İKİNCİ EKRAN BULUNDU:', {
                  left: targetLeft, top: targetTop, width: targetWidth, height: targetHeight
                });
              } else {
                console.log('📺 [CustomerDisplay] İkinci ekran bulunamadı, birincil ekranın sağına açılacak');
              }
            }
          } else {
            console.log('📺 [CustomerDisplay] Screen API desteklenmiyor, birincil ekranın sağına açılacak');
          }
        } catch (screenErr) {
          console.warn('📺 [CustomerDisplay] Ekran bilgisi alınamadı:', screenErr);
        }

        if (!isMounted) return;

        const windowFeatures = [
          `width=${targetWidth}`,
          `height=${targetHeight}`,
          `left=${targetLeft}`,
          `top=${targetTop}`,
          'menubar=no',
          'toolbar=no',
          'location=no',
          'status=no',
          'resizable=yes',
          'scrollbars=no',
        ].join(',');

        const newWindow = window.open(displayUrl, 'CustomerDisplay', windowFeatures);

        if (newWindow && isMounted) {
          // @ts-ignore
          window.__customerDisplayWindow = newWindow;
          console.log('✅ [CustomerDisplay] Müşteri ekranı penceresi açıldı');

          setTimeout(() => {
            try {
              if (newWindow && !newWindow.closed) {
                newWindow.moveTo(targetLeft, targetTop);
                newWindow.resizeTo(targetWidth, targetHeight);
                console.log('📺 [CustomerDisplay] Pencere konumlandırıldı');
              }
            } catch (posErr) {
              console.warn('📺 [CustomerDisplay] Pencere ayarlanamadı:', posErr);
            }
          }, 500);
        } else if (!newWindow) {
          console.warn('⚠️ [CustomerDisplay] Pencere açılamadı (popup blocker?)');
        }
      } catch (err) {
        console.error('❌ [CustomerDisplay] Müşteri ekranı açılırken hata (oturum etkilenmeyecek):', err);
      }
    };

    const timer = setTimeout(checkAndOpenCustomerDisplay, 2000);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [currentScreenId, companyId, isPackageMode, isRestaurantMode]);

  // ✅ Satış modunda olduğumuzu işaretle (RestaurantScreen ile paylaşım için)
  useEffect(() => {
    // Restoran modundan geliyorsak, satış modunu güncelle
    if (isRestaurantMode) {
      sessionStorage.setItem('__inSalesMode', 'quicksale');
      console.log('📺 [QuickSaleScreen] Satış modu güncellendi: quicksale (restoran)');
    }

    return () => {
      // QuickSaleScreen'den çıkılıyor - RestaurantScreen'e dönülüyorsa işaretle
      if (isRestaurantMode) {
        // Restoran moduna geri dönüyoruz
        sessionStorage.setItem('__inSalesMode', 'restaurant');
        console.log('📺 [QuickSaleScreen] Restoran ekranına geri dönülüyor');
      }
    };
  }, [isRestaurantMode]);

  // Sepet değişikliklerinde müşteri ekranına gönder
  useEffect(() => {
    if (!customerDisplayChannelRef.current) return;

    // ✅ Paket sipariş modunda müşteri ekranını PackageOrderScreen yönetir.
    // Bu modaldan sepet broadcast yapılmaz; aksi halde paket ekranındaki
    // müşteri görüntüsü sürekli sepet görünümüne döner ve reklam dönmeyi keser.
    if (isPackageMode) {
      return;
    }

    const total = calculateTotalHook();
    const cartData = cart.map(item => ({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
    }));

    // ✅ Mesaj gönder - sepette ürün varsa süresiz sepet göster, yoksa reklama dön
    customerDisplayChannelRef.current.postMessage({
      type: cart.length > 0 ? 'CART_UPDATE' : 'HIDE_CART',
      cart: cartData,
      total: total,
      screenId: currentScreenId ? parseInt(currentScreenId) : undefined,
      // ✅ YENİ: Sepette ürün olduğu sürece sepet görünsün (süre sınırı yok)
      keepCartVisible: cart.length > 0,
    });

    console.log('📨 [CustomerDisplay] Sepet güncellendi:', { itemCount: cart.length, total, keepVisible: cart.length > 0 });
  }, [cart, currentScreenId, calculateTotalHook, isPackageMode]);

  // Satış tamamlandığında müşteri ekranına bildir
  const notifyCustomerDisplaySaleComplete = useCallback(() => {
    if (customerDisplayChannelRef.current) {
      customerDisplayChannelRef.current.postMessage({
        type: 'SALE_COMPLETE',
        cart: [],
        total: 0,
      });
      console.log('📨 [CustomerDisplay] Satış tamamlandı bildirimi gönderildi');
    }
  }, []);

  return { customerDisplayChannelRef, notifyCustomerDisplaySaleComplete };
};

export default useCustomerDisplay;
